import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import { toast } from "react-toastify";
import { Container, Button } from "react-bootstrap";

import { BASE_URL, ROOT_URL } from "../../../config";
import Turnstile from "../../../shared/components/Turnstile";
import logo from "../../../assets/images/logo-new.png";

// How often the page asks which round is live, so a fan who keeps the page open sees the next
// question without scanning the QR code again.
const REFRESH_MS = 5000;
// The bot check takes a moment after the page opens: a tap on an answer waits this long for it.
const TOKEN_POLL_MS = 250;
const TOKEN_POLL_TRIES = 60;

const Logo = () => (
  <div style={{ textAlign: "center" }} className="survey-logo-container">
    <img src={logo} alt="logo" />
  </div>
);

// The answers a round can have: two, or three when it has an optional third one such as "Draw".
const ANSWER_KEYS = ["option1_votes", "option2_votes", "option3_votes"];

// The round's logo or ad, shown under the answers. With a website, tapping the image opens it in a new tab
// (the backend only stores http(s) addresses; this checks again before putting one in a link).
const RoundAd = ({ ad }) => {
  if (!ad || !ad.image) {
    return null;
  }
  const image = (
    <img
      src={`${ROOT_URL}${ad.image}`}
      alt="Sponsor"
      style={{ maxWidth: "100%", maxHeight: 240, objectFit: "contain" }}
    />
  );
  return (
    <div className="text-center mt-3 mb-2" data-round-ad>
      {ad.link && /^https?:\/\//i.test(ad.link) ? (
        <a href={ad.link} target="_blank" rel="noopener noreferrer sponsored">
          {image}
        </a>
      ) : image}
    </div>
  );
};

RoundAd.propTypes = {
  ad: PropTypes.shape({ image: PropTypes.string, link: PropTypes.string }),
};

RoundAd.defaultProps = {
  ad: null,
};

// Voting page of an Event Survey (fetch_survey answers with eventData): shows only the live round's
// question with its two answers. One tap votes. The page keeps asking which round is live; the
// backend refuses a vote for any other round, so a stale page can never count in the wrong round.
const EventSurveyVote = ({ surveyCode, initialData, onExpired }) => {
  const [data, setData] = useState(initialData);
  // The round this page already voted in, and the answer waiting for the bot check to finish.
  const [votedRound, setVotedRound] = useState(null);
  const [pending, setPending] = useState(null);
  const [sending, setSending] = useState(false);
  const turnstile = useRef(null);

  const { eventData, questionsData, surveyData } = data;
  const round = eventData.current_round;
  const question = questionsData && questionsData.length ? questionsData[0] : null;
  const closed = eventData.closed || !question;
  const ad = question ? question.ad : null;

  const refresh = useCallback(() => (
    axios
      .post(`${BASE_URL}/fetch_survey`, { surveyCode })
      .then((res) => {
        if (res.data.error || !res.data.eventData) {
          // Expired, deleted, or no longer an event: the survey page shows the usual message.
          onExpired(res.data);
        } else {
          setData(res.data);
        }
      })
      // A failed poll (weak signal) is not shown: the next one tries again.
      .catch(() => { })
  ), [surveyCode, onExpired]);

  useEffect(() => {
    const poll = () => {
      if (!document.hidden) {
        refresh();
      }
    };
    const intervalId = setInterval(poll, REFRESH_MS);
    document.addEventListener("visibilitychange", poll);
    return () => {
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", poll);
    };
  }, [refresh]);

  // A new round clears the "voted" note...
  useEffect(() => {
    setVotedRound((voted) => (voted === round ? voted : null));
  }, [round]);

  // ...and an answer picked for the old round is dropped instead of counting in the new one.
  useEffect(() => {
    if (pending && pending.round !== round) {
      setPending(null);
      toast.warn("The round changed. Please vote on the new question.");
    }
  }, [pending, round]);

  const send = useCallback((optionKey, token) => {
    setSending(true);
    axios
      .post(`${BASE_URL}/survey_vote`, {
        surveyCode,
        turnstile_token: token,
        allAnswers: [{ questionId: question.questions.question_id, optionVote: optionKey }],
      })
      .then((res) => {
        if (res.data.error) {
          toast.error(res.data.message);
          if (res.data.round_changed) {
            refresh();
          }
        } else {
          setVotedRound(round);
        }
      })
      .catch(() => toast.error("Your vote could not be sent. Please try again."))
      .then(() => {
        // A bot-check token works once, so the next vote needs a new one.
        if (turnstile.current) {
          turnstile.current.reset();
        }
        setSending(false);
      });
  }, [surveyCode, question, round, refresh]);

  const onAnswer = (optionKey) => {
    if (sending || pending) {
      return;
    }
    const token = turnstile.current ? turnstile.current.getToken() : "";
    if (token) {
      send(optionKey, token);
    } else {
      setPending({ optionKey, round });
    }
  };

  useEffect(() => {
    if (!pending) {
      return undefined;
    }
    let tries = 0;
    const intervalId = setInterval(() => {
      tries += 1;
      const token = turnstile.current ? turnstile.current.getToken() : "";
      if (token) {
        clearInterval(intervalId);
        setPending(null);
        send(pending.optionKey, token);
      } else if (tries >= TOKEN_POLL_TRIES) {
        clearInterval(intervalId);
        setPending(null);
        toast.warn("Please complete the human verification, then tap your answer again.");
      }
    }, TOKEN_POLL_MS);
    return () => clearInterval(intervalId);
  }, [pending, send]);

  let body;
  if (closed) {
    body = (
      <div className="text-center p-3">
        <h5>Voting is closed right now</h5>
        <p className="mb-0">The next question appears here automatically.</p>
      </div>
    );
  } else if (votedRound === round) {
    body = (
      <div className="text-center p-3">
        <h5>Thanks! Your vote for round {round} is in.</h5>
        <p className="mb-0">The next question appears here automatically.</p>
        <RoundAd ad={ad} />
      </div>
    );
  } else {
    const busy = sending || !!pending;
    body = (
      <>
        <p className="mb-1 text-center" style={{ color: "gray" }}>
          Round {round} of {eventData.total_rounds}
        </p>
        <h4 className="text-center mb-4">
          <b>{question.questions.question_title}</b>
        </h4>
        {ANSWER_KEYS.filter((optionKey) => question.options[optionKey]).map((optionKey) => (
          <Button
            key={optionKey}
            block
            size="lg"
            variant="outline-primary"
            className="mb-3 py-3"
            disabled={busy}
            onClick={() => onAnswer(optionKey)}
          >
            {question.options[optionKey]}
          </Button>
        ))}
        {busy && (
          <p className="text-center" style={{ color: "gray" }}>
            {sending ? "Sending your vote..." : "Checking you are human..."}
          </p>
        )}
        <RoundAd ad={ad} />
      </>
    );
  }

  return (
    <Container className="form-container padding_remove_mobile">
      <Logo />
      <div className="p-2 mb-2 mt-2">
        <h5 className="m-0 text-center">
          <b>{surveyData.survey_title}</b>
        </h5>
      </div>
      <hr />
      {body}
      <div className="mt-4" style={{ textAlign: "center" }}>
        <Turnstile ref={turnstile} />
        <div className="pb-5 pt-3">
          <Logo />
        </div>
      </div>
    </Container>
  );
};

EventSurveyVote.propTypes = {
  surveyCode: PropTypes.string.isRequired,
  initialData: PropTypes.instanceOf(Object).isRequired,
  onExpired: PropTypes.func.isRequired,
};

export default EventSurveyVote;
